import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import api from "../services/api";
import { Trash2, ClipboardPaste } from "lucide-react";
import { isAgent } from "../services/auth";
import PageHeader from "../components/common/PageHeader";
import Button from "../components/common/Button";
import AddCustomersPopup from "../components/AddCustomersPopup.js";
import QuickImportModal from "../components/QuickImportModal";
import LoadingSpinner from "../components/LoadingSpinner";
import Pagination from "../components/Pagination";
import { toast } from "react-toastify";

const GroupMembers = () => {
  const agentRole = isAgent();
  const { groupId } = useParams();
  const popupRef = useRef();
  const [customers, setCustomers] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [allCustomers, setAllCustomers] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [showQuickImport, setShowQuickImport] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const membersPerPage = 10;

  // Fetch customers and group details
  const fetchCustomers = async () => {
    try {
      const [res, allCustomersRes] = await Promise.all([
        api.get(`/group-members/${groupId}/customers`),
        api.get("/customers/by-user"),
      ]);
      setGroupName(res.data.group?.name || "Customer Group");
      setCustomers(res.data.customers);
      setAllCustomers(allCustomersRes.data.customers);
    } catch (err) {
      console.error("Error fetching customers", err);
    } finally {
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers(); // Fetch customers when the component mounts
  }, [groupId]);

  // Handle click outside popup to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setShowPopup(false);
      }
    };

    if (showPopup) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPopup]);

  // Handle selection and deselection of customers
  const handleCustomerSelect = (customerId) => {
    setSelectedCustomers((prevSelected) =>
      prevSelected.includes(customerId)
        ? prevSelected.filter((id) => id !== customerId)
        : [...prevSelected, customerId]
    );
  };

  // Handle adding selected customers to the group
  const handleAddCustomers = async () => {
    if (selectedCustomers.length === 0) {
      toast.warning("Please select at least one customer to add.");
      return;
    }

    setLoading(true);
    try {
      const res = await api.post(`/group-members/add-multiple/${groupId}`, {
        customerIds: selectedCustomers,
      });

      if (res.data.success) {
        toast.success("Customers successfully added to the group!");
        setShowPopup(false);
        setSelectedCustomers([]);
        const addedCustomers = allCustomers.filter((customer) =>
          selectedCustomers.includes(customer.id)
        );
        setCustomers((prev) => [...prev, ...addedCustomers]);
      } else {
        toast.error("Failed to add customers to the group. Please try again.");
      }
    } catch (err) {
      console.error("Error adding customers to group", err);
      toast.error("An error occurred while adding customers.");
    } finally {
      setLoading(false);
    }
  };

  // Handle select/deselect all customers
  const handleSelectAll = () => {
    const availableCustomerIds = allCustomers
      .filter((customer) => !customers.some((c) => c.id === customer.id))
      .map((c) => c.id);

    if (availableCustomerIds.every((id) => selectedCustomers.includes(id))) {
      setSelectedCustomers((prev) =>
        prev.filter((id) => !availableCustomerIds.includes(id))
      );
    } else {
      setSelectedCustomers((prev) => [
        ...prev,
        ...availableCustomerIds.filter((id) => !prev.includes(id)),
      ]);
    }
  };

  // ✅ Handle deleting a customer from the group
  const handleDeleteCustomer = async (customerId) => {
    if (
      !window.confirm(
        "Are you sure you want to remove this customer from the group?"
      )
    ) {
      return;
    }

    try {
      const res = await api.delete(
        `/group-members/${groupId}/customer/${customerId}`
      );

      if (res.data.success) {
        toast.success("Customer removed from group!");
        setCustomers((prev) => prev.filter((c) => c.id !== customerId));
      } else {
        toast.error("Failed to remove customer. Please try again.");
      }
    } catch (err) {
      console.error("Error deleting customer:", err);
      toast.error("An error occurred while deleting the customer.");
    }
  };

  if (initialLoading) {
    return <LoadingSpinner fullScreen size="lg" text="Loading members..." />;
  }

  return (
    <div className="p-4 sm:p-8 relative">
      {/* Header Section */}
      <PageHeader
        title={groupName || "Customer Group"}
        subtitle={`${customers.length} member${customers.length !== 1 ? "s" : ""}`}
        action={
          !agentRole && (
            <div className="relative flex items-center gap-2">
              <Button
                variant="secondary"
                onClick={() => setShowQuickImport(true)}
                icon={<ClipboardPaste className="w-4 h-4" />}
              >
                Quick Import
              </Button>
              <Button variant="primary" onClick={() => setShowPopup(!showPopup)}>
                Add Customers
              </Button>

              {showPopup && (
                <AddCustomersPopup
                  allCustomers={allCustomers}
                  customers={customers}
                  selectedCustomers={selectedCustomers}
                  handleSelectAll={handleSelectAll}
                  handleCustomerSelect={handleCustomerSelect}
                  handleAddCustomers={handleAddCustomers}
                  setShowPopup={setShowPopup}
                  loading={loading}
                />
              )}
            </div>
          )
        }
      />

      {/* Customers Table */}
      <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
        {customers.length === 0 ? (
          <p className="text-gray-600 text-center py-8">No customers found.</p>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full table-auto border-collapse">
                <thead>
                  <tr>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide bg-gray-100 border-b">#</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide bg-gray-100 border-b">Name</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide bg-gray-100 border-b">Phone Number</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide bg-gray-100 border-b">Location</th>
                    {!agentRole && (
                      <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide bg-gray-100 border-b">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {customers
                    .slice((currentPage - 1) * membersPerPage, currentPage * membersPerPage)
                    .map((customer, index) => (
                      <tr
                        key={customer.id}
                        className={`hover:bg-gray-50 ${customer.status === false ? "opacity-50" : ""}`}
                      >
                        <td className="px-4 py-3 text-sm text-gray-500">{(currentPage - 1) * membersPerPage + index + 1}</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{customer.name}</td>
                        <td className="px-4 py-3 text-sm font-mono text-gray-700">{customer.phoneNumber}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{customer.location || "--"}</td>
                        {!agentRole && (
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => handleDeleteCustomer(customer.id)}
                              className="text-red-500 hover:text-red-700 transition flex items-center gap-1 ml-auto text-sm"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              Remove
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                </tbody>
              </table>
              <Pagination
                currentPage={currentPage}
                totalPages={Math.ceil(customers.length / membersPerPage)}
                onPageChange={setCurrentPage}
              />
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-4">
              {customers.map((customer, index) => (
                <div
                  key={customer.id}
                  className={`bg-gray-50 rounded-lg p-4 border ${
                    customer.status === false ? "opacity-50" : ""
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-semibold text-gray-900">
                      {index + 1}. {customer.name}
                    </h3>
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Phone:</span>
                      <span className="text-gray-900">
                        {customer.phoneNumber}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Location:</span>
                      <span className="text-gray-900">
                        {customer.location || "--"}
                      </span>
                    </div>
                  </div>

                  {!agentRole && (
                    <div className="flex justify-end mt-3">
                      <button
                        onClick={() => handleDeleteCustomer(customer.id)}
                        className="text-sm text-red-600 hover:text-red-800 flex items-center gap-1"
                      >
                        <Trash2 className="w-4 h-4" />
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      {showQuickImport && (
        <QuickImportModal
          groupId={groupId}
          onClose={() => setShowQuickImport(false)}
          onSuccess={() => { setShowQuickImport(false); fetchCustomers(); }}
        />
      )}
    </div>
  );
};

export default GroupMembers;
